// src/lib/form-cover-photo.js — photo de couverture du formulaire d'accueil :
// une ADRESSE dans le stockage de fichiers, plus jamais un base64 dans la config.
//
// ── POURQUOI (coupure de la base, 2026-09-14) ───────────────────────────────
// `cfg_config.data` pesait 1 407 447 octets, dont 1 373 198 (97,6 %) pour DEUX
// photos de couverture encodées en base64 dans `forms.unified.cover_photo_b64`.
// Relues en entier des centaines de fois par jour par des tâches qui ne les
// regardent jamais → 27 Go de sortie, quota dépassé, base en 402.
//
// ── LE MODÈLE ───────────────────────────────────────────────────────────────
// `forms.unified.cover_photo_url`  = URL publique de l'objet (source de vérité)
// `forms.unified.cover_photo_path` = chemin dans le bucket (pour rollback/purge)
// `forms.unified.cover_photo_b64`  = VESTIGE, accepté en lecture tant que le
//                                    script de bascule ne l'a pas retiré.
//
// PUBLIC, pas privé : cette image est le bandeau d'un formulaire servi à un
// voyageur ANONYME (api/form-config.js, sur simple token) ; elle ne porte
// aucune donnée personnelle — c'est une photo du logement. Une URL signée
// expirerait sur une page que le voyageur garde ouverte ou recharge des jours
// plus tard → image cassée. Bucket = `logements-photos` (public, RLS admin en
// écriture, lecture anon), patron ADR-041 (l'ancien `src/lib/photos-api.js`,
// sans utilisateur, a été retiré le 03/10), sous le
// préfixe `{logement_id}/form-cover/` pour ne jamais se mêler à la galerie.

import { supabase } from '../supabase'

export const COVER_BUCKET = 'logements-photos'
export const COVER_MAX_BYTES = 5 * 1024 * 1024 // 5 Mo (bucket : 10 Mo)

const DATA_IMAGE_RE = /^\s*data:image\//i

/** Une chaîne est-elle une image encodée en base64 (`data:image/...`) ? */
export function isDataImage(value) {
  return typeof value === 'string' && DATA_IMAGE_RE.test(value)
}

/**
 * La source à afficher pour la couverture d'un formulaire.
 *
 * L'ADRESSE GAGNE sur le base64 quand les deux sont présents : pendant la
 * bascule, le script pose l'adresse EN PREMIER (le base64 reste comme filet)
 * puis retire l'octet dans une seconde passe. Si le base64 gagnait, on
 * n'aurait aucun moyen de vérifier en prod que l'adresse fonctionne AVANT de
 * détruire le filet. Le retour arrière, lui, est trivial : on efface l'adresse
 * et le base64 reprend.
 *
 * @param {object|null} form — `forms.unified` (ou un formConfig qui en dérive)
 * @returns {string|null} URL ou data-URL, ou null si aucune couverture
 */
export function resolveCoverPhoto(form) {
  if (!form || typeof form !== 'object') return null
  if (typeof form.cover_photo_url === 'string' && form.cover_photo_url.trim()) {
    return form.cover_photo_url.trim()
  }
  if (typeof form.cover_photo_b64 === 'string' && form.cover_photo_b64.trim()) {
    return form.cover_photo_b64
  }
  return null
}

/** Nom d'objet unique (UUID quand disponible, repli horodaté sinon — jsdom). */
function nomUnique() {
  const c = typeof window !== 'undefined' ? window.crypto : null
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/** Chemin d'objet : `{logement_id}/form-cover/{nom}.{ext}`. */
export function coverPhotoPath(logementId, name, ext) {
  return `${logementId}/form-cover/${name}.${ext}`
}

/**
 * Téléverse une couverture dans le bucket (client admin, RLS
 * `logements_photos_admin_all`) et renvoie la patch à FUSIONNER dans
 * `forms.unified` : l'adresse, le chemin, et le base64 mis à null — jamais
 * plus un octet d'image dans la configuration.
 *
 * @returns {Promise<{cover_photo_url:string, cover_photo_path:string, cover_photo_b64:null}>}
 */
export async function uploadCoverPhoto(logementId, file) {
  if (!logementId) throw new Error('Logement inconnu')
  if (!file) throw new Error('Pas de fichier')
  if (!file.type || !file.type.startsWith('image/')) throw new Error('Le fichier doit être une image')
  if (file.size > COVER_MAX_BYTES) {
    throw new Error(`Trop volumineux (${(file.size / 1024).toFixed(0)} Ko > ${COVER_MAX_BYTES / 1024} Ko max)`)
  }
  const ext = ((file.name || '').split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg'
  const path = coverPhotoPath(logementId, nomUnique(), ext)
  const { error } = await supabase.storage.from(COVER_BUCKET).upload(path, file, {
    cacheControl: '31536000', // nom unique → immuable
    upsert: false,
    contentType: file.type,
  })
  if (error) throw new Error(error.message || 'Téléversement échoué')
  const { data } = supabase.storage.from(COVER_BUCKET).getPublicUrl(path)
  return { cover_photo_url: data.publicUrl, cover_photo_path: path, cover_photo_b64: null }
}

/** Patch pour retirer la couverture (l'objet reste dans le bucket : inerte, jamais relu). */
export function clearCoverPhotoPatch() {
  return { cover_photo_url: null, cover_photo_path: null, cover_photo_b64: null }
}
